// Shared by CreateModal and the /create studio so both send the exact same
// POST /api/v2/conversion body. The backend is untouched by the studio; if
// the request shape changes, change it here and both surfaces follow.
import UpgradeModal from "./UpgradeModal";
import CreditPackModal from "./CreditPackModal";

export const MAX_SONGS = 1; // Maximum number of songs allowed for a mashup
export const MAX_VOICES = 6; // Duets/groups

// --- Settings Reducer ---
export const initialSettingsState = {
  pitchScaling: {}, // { voiceId: pitchValue, ... }
  addEffects: true,
  voiceVolume: 0,
  extractBackground: false,
  skipExtraction: false,
  highQuality: false,
  name: "",
  autotune: {
    enabled: false,
    strength: 1,
    input_key: "auto",
    subscale: "scale",
    notes_roll: 0,
  },
  selectedTab: "basic",
  selectedEffectId: null, // <-- Reverted to selectedEffectId: "none"
};

export function settingsReducer(state, action) {
  switch (action.type) {
    case "SET_PITCH_SCALING":
      return {
        ...state,
        pitchScaling: {
          ...state.pitchScaling,
          [action.payload.voiceId]: action.payload.pitchValue,
        },
      };
    case "REMOVE_PITCH_SCALING": {
      const newPitchScaling = { ...state.pitchScaling };
      delete newPitchScaling[action.payload.voiceId];
      return { ...state, pitchScaling: newPitchScaling };
    }

    case "SET_VOICE_VOLUME":
      return { ...state, voiceVolume: action.payload };
    case "SET_EXTRACT_BACKGROUND":
      return { ...state, extractBackground: action.payload };
    case "SET_SKIP_EXTRACTION":
      return { ...state, skipExtraction: action.payload };
    case "SET_HIGH_QUALITY":
      return { ...state, highQuality: action.payload };
    case "SET_NAME":
      return { ...state, name: action.payload };
    case "SET_AUTOTUNE": // Expects payload like { enabled: boolean, strength: number }
      return { ...state, autotune: { ...state.autotune, ...action.payload } };
    case "SET_AUTOTUNE_ENABLED":
      return {
        ...state,
        autotune: { ...state.autotune, enabled: action.payload },
      };
    case "SET_AUTOTUNE_STRENGTH":
      return {
        ...state,
        autotune: { ...state.autotune, strength: action.payload },
      };
    case "SET_SELECTED_TAB":
      return { ...state, selectedTab: action.payload };
    case "SET_AUDIO_EFFECT":
      // payload is now the effectId (string)
      return {
        ...state,
        selectedEffectId: action.payload, // <-- Update selectedEffectId with payload (effectId)
      };
    default:
      console.warn("Unhandled settings action type:", action.type);
      return state;
    // throw new Error(`Unhandled action type: ${action.type}`);
  }
}
// --- End Settings Reducer ---

// True if user does NOT have paid credits but DOES have free credits.
export const isFreePlanQuota = (user) => {
  const quotaConversions = user?.quota?.conversions;
  return (
    quotaConversions?.hasCredits === false &&
    quotaConversions?.hasFreeCredits === true
  );
};

export const getConversionName = (settings, selectedSongsList) => {
  const firstSongForNaming = selectedSongsList[0];
  let conversionName =
    settings.name || firstSongForNaming?.title || "Untitled Cover";
  if (selectedSongsList.length > 1 && !settings.name) {
    conversionName += " Mashup";
  }
  return conversionName;
};

// The POST /api/v2/conversion body.
export const buildConversionRequestBody = ({
  settings,
  selectedVoices,
  selectedSongsList,
  marbleRunTemplate,
  voiceTimes,
}) => {
  // For AI-generated songs we don't pre-poll here anymore — the backend
  // creates the conversion in `pending_source` state, submits Treblo,
  // and returns a shortid immediately. The conversion loading page drives
  // /api/v2/conversion/advance/[id] until the audio is ready and the
  // RunPod job has been kicked off. This lets the user land on the main
  // loading page (and refresh it) right after clicking Generate.
  const songInputs = selectedSongsList.map((song) => {
    const input = {
      source_mode: song.sourceMode,
      title: song.title,
    };
    if (song.sourceMode === "tts") {
      input.text = song.metadata.text;
      input.gender = song.metadata.gender;
      input.accent = song.metadata.accent;
      input.expression = song.metadata.expression;
    } else if (song.sourceMode === "generate") {
      if (song.url) {
        // Pre-resolved (e.g. retry of a previous generation).
        input.input_url = song.url;
      } else {
        // Async generation — backend resolves this server-side.
        input.caption = song.metadata?.caption;
        input.lyrics = song.metadata?.lyrics;
        input.duration = song.metadata?.duration;
      }
    } else {
      input.input_url = song.url;
      input.uploaded_file_id = song.uploaded_file_id || song._id || null;
    }
    return input;
  });

  return {
    name: getConversionName(settings, selectedSongsList),
    voices: selectedVoices.map((v) => ({
      _id: v._id,
      model_id: v.urlParam,
      pitchScaling: settings.pitchScaling[v._id] ?? 0,
    })),
    settings: settings, // This includes selectedEffectId, autotune, etc.
    song_inputs: songInputs, // Array of song inputs
    // input_url and uploaded_file_id are now part of song_inputs
    selected_effect_id: settings.selectedEffectId, // Retained for now, though part of settings
    // Who sings when, chosen in the studio (WhoSings); else the jammer decides.
    ...(voiceTimes?.length ? { voice_times: voiceTimes } : {}),
    template:
      marbleRunTemplate?.type === "marble_run"
        ? marbleRunTemplate?.config?.template
        : marbleRunTemplate?.template,
    videoTemplate:
      marbleRunTemplate?.type === "external"
        ? { type: "external", config: marbleRunTemplate.config }
        : marbleRunTemplate?.template
          ? { type: "marble_run" }
          : undefined,
  };
};

// Optimistic quota/credit patch applied with updateUser() once the POST
// returns a shortid.
export const getOptimisticUserUpdate = (user, marbleRunTemplate) => {
  const isFree = isFreePlanQuota(user);
  const updatedQuota = {
    ...user.quota,
    conversions: {
      ...user.quota.conversions,
      remainingFreeCredits: isFree
        ? (user?.quota?.conversions?.remainingFreeCredits ?? 0) - 1
        : user?.quota?.conversions?.remainingFreeCredits,
    },
  };

  // Optimistic coin deduction for marble run
  if (
    marbleRunTemplate &&
    user.quota?.marbleRun &&
    !user.quota.marbleRun.isUnlimited
  ) {
    const newCoins = Math.max(
      (user.quota.marbleRun.remainingCoins ?? 0) - 1,
      0,
    );
    updatedQuota.marbleRun = {
      ...user.quota.marbleRun,
      remainingCoins: newCoins,
      hasCoins: newCoins > 0,
    };
  }

  return {
    credits: isFree ? user.credits : user.credits - 1,
    coins:
      marbleRunTemplate && !user.quota?.marbleRun?.isUnlimited
        ? Math.max((user.coins ?? 0) - 1, 0)
        : user.coins,
    quota: updatedQuota,
  };
};

export const openUpgradeModal = (openModal, message) =>
  openModal(
    "upgrade-modal",
    <UpgradeModal from="create-modal" dailyLimit={false} message={message} />,
    { className: "tw-p-0" },
  );

// Maps the POST's error message to the paywall/credit modal CreateModal has
// always shown. Returns true if it handled the message with a modal.
export const handleConversionErrorMessage = ({
  errorMessage,
  openModal,
  hasCreatorOrPowerPlan,
  toastError,
}) => {
  if (errorMessage === "You need to upgrade to use Text to Speech!") {
    openUpgradeModal(openModal, "Upgrade to use Text to Speech!");
  } else if (errorMessage === "You are out of credits") {
    openUpgradeModal(openModal, "Upgrade to continue!");
  } else if (errorMessage?.includes("Coins")) {
    if (hasCreatorOrPowerPlan) {
      openModal("credit-pack-modal", <CreditPackModal />, {
        className: "md:tw-w-[480px] !tw-p-0 tw-overflow-hidden",
      });
    } else {
      openUpgradeModal(
        openModal,
        "Upgrade to Creator to get Coins for Marble Run!",
      );
    }
  } else if (errorMessage) {
    toastError(errorMessage);
  }
};
